import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Card, CircularProgress, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import {
  PersonOutlined as UserIcon,
  SettingsOutlined as SystemIcon,
  AlternateEmail as ApiIcon,
  Call as CallIcon,
  MailOutline as MailOutlineIcon,
  EventAvailable as EventAvailableIcon,
  EditNote as EditNoteIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  fetchLeadActivities,
  type LeadActivityV2,
  type ActivityFieldChange,
  type PaginationMeta,
} from "./leadApi";
import UserAvatar from "../../Components/UserAvatar";

/* ── Date-bucket helper ─────────────────────────────────────────────────
   Groups the chronological activity feed into operator-friendly buckets
   so reps can scan history by "when" instead of by absolute date. The
   labels mirror what staff say out loud: "Today", "Yesterday", "Earlier
   this week", "Older". */
type DateBucket = "today" | "yesterday" | "this_week" | "older";

function bucketOf(iso: string): DateBucket {
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "older";
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const dayMs = 86_400_000;
  const startOfToday     = now.getTime();
  const startOfYesterday = startOfToday - dayMs;
  const startOfWeek      = startOfToday - 6 * dayMs;
  if (t >= startOfToday)     return "today";
  if (t >= startOfYesterday) return "yesterday";
  if (t >= startOfWeek)      return "this_week";
  return "older";
}

const BUCKET_LABEL: Record<DateBucket, string> = {
  today:     "Today",
  yesterday: "Yesterday",
  this_week: "Earlier this week",
  older:     "Older",
};

/* ── Design tokens ─────────────────────────────────────────────────────── */

const NAVY = "#0d1b2a";

/* ── Type helpers ──────────────────────────────────────────────────────── */

type FilterTab =
  | "all"
  | "calls"
  | "emails"
  | "meetings"
  | "notes"
  | "status"
  | "assignments"
  | "follow_ups"
  | "system";

interface ActivityGroup {
  requestId: string | null;
  items: LeadActivityV2[];
}

/* ── Config maps ───────────────────────────────────────────────────────── */

function accentColor(type: string): string {
  if (type === "created")                    return "#3b82f6";
  if (type === "status_changed")             return "#8b5cf6";
  if (type === "assigned")                   return "#14b8a6";
  if (type === "note_added")                 return "#f59e0b";
  if (type === "updated")                    return "#6366f1";
  if (type === "contact_logged")             return "#16a34a";
  if (type === "task_created")               return "#3b82f6";
  if (type === "task_completed")             return "#16a34a";
  if (type === "task_updated")               return "#64748b";
  if (type.includes("email"))                return "#0ea5e9";
  // Same-event grouping + customer-link palette (matches LeadDetailPage)
  if (type === "lead_primary_event")         return "#b45309";
  if (type === "lead_flagged_same_event")    return "#6d28d9";
  if (type === "lead_same_event_linked")     return "#6d28d9";
  if (type === "duplicate_prevented")        return "#0ea5e9";
  return "#94a3b8";
}

function actionLabel(type: string, meta: Record<string, any> | null | undefined): string {
  // contact_logged is type-aware — surface the contact method as the
  // human label ("Call Logged" / "Email Logged" / etc.) so the row reads
  // as the actual operator action instead of a generic "Contact logged".
  if (type === "contact_logged") {
    const sub = String(meta?.type ?? "").toLowerCase();
    if (sub === "call" || sub === "phone")    return "Call Logged";
    if (sub === "email")                       return "Email Logged";
    if (sub === "meeting" || sub === "in_person") return "Meeting Logged";
    if (sub === "note")                        return "Note Logged";
    if (sub === "voicemail")                   return "Voicemail Left";
    return "Contact Logged";
  }
  const map: Record<string, string> = {
    created:                 "Lead created",
    status_changed:          "Status changed",
    assigned:                "Lead assigned",
    note_added:              "Note added",
    updated:                 "Lead updated",
    task_created:            "Follow-up scheduled",
    task_completed:          "Task completed",
    task_updated:            "Task updated",
    email_linked:            "Email linked",
    email_unlinked:          "Email unlinked",
    email_sent:              "Email sent",
    sms_sent:                "SMS sent",
    sms_failed:              "SMS failed",
    // Reframed for the always-create customer-linking flow. The old
    // "Duplicate detected" wording implied the new lead was rejected.
    duplicate_prevented:     "Related enquiry detected",
    inquiry_received:        "Inquiry received",
    lead_primary_event:      "★ Marked as primary event",
    lead_flagged_same_event: "Flagged as same event",
    lead_same_event_linked:  "Same-event lead linked",
  };
  return (
    map[type] ??
    type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/* Contact method icon — drives the leading visual on contact_logged rows
   so the operator can scan the timeline and instantly see whether each
   touchpoint was a call, email, meeting, or note. */
function contactMethodIcon(method: string): React.ReactNode {
  const sx = { fontSize: 14, color: "#16a34a" };
  const m = (method || "").toLowerCase();
  if (m === "call" || m === "phone")          return <CallIcon sx={sx} />;
  if (m === "email")                          return <MailOutlineIcon sx={sx} />;
  if (m === "meeting" || m === "in_person")   return <EventAvailableIcon sx={sx} />;
  if (m === "note")                           return <EditNoteIcon sx={sx} />;
  if (m === "voicemail")                      return <CallIcon sx={{ ...sx, color: "#f59e0b" }} />;
  return <HistoryIcon sx={sx} />;
}

/* Human-readable outcome label. The DB stores snake_case enums; this
   converts them to the title-case wording the operator typed in the
   modal (and matches the chip styling shown in the form). */
function formatOutcome(outcome: string | null | undefined): string {
  if (!outcome) return "";
  return outcome
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function matchesFilter(a: LeadActivityV2, filter: FilterTab): boolean {
  // Sub-type of a contact_logged row (call / email / meeting / note),
  // stored in meta.type by LeadContactController. Falsy for all other
  // activity types — used by the tab routing below.
  const sub = String(a.meta?.type ?? "").toLowerCase();

  switch (filter) {
    case "calls":
      return a.type === "contact_logged" && (sub === "call" || sub === "phone" || sub === "voicemail");

    case "emails":
      // Both contact_logged-as-email AND any email_* activity row.
      return (a.type === "contact_logged" && sub === "email")
        || a.type.includes("email");

    case "meetings":
      return a.type === "contact_logged" && (sub === "meeting" || sub === "in_person");

    // Notes tab surfaces standalone internal notes AND any contact_logged
    // row that carries typed context — the rep thinks of both as "what
    // was said about this lead". Empty contact rows still appear in "All".
    case "notes":
      return a.type === "note_added"
        || (a.type === "contact_logged" && !!a.meta?.notes)
        || (a.type === "contact_logged" && sub === "note");

    case "status":
      return a.type === "status_changed";

    case "assignments":
      return a.type === "assigned";

    case "follow_ups":
      return a.type === "task_created"
        || a.type === "task_completed"
        || a.type === "task_updated"
        || a.type === "task_rescheduled"
        || a.type === "task_cancelled";

    // Non-user-driven rows (auto-promotions, classifier runs, dedupe
    // hits, observer fallbacks). The actor_type is the authoritative
    // signal — anything not stamped as "user" lives here.
    case "system":
      return a.actor_type !== "user";

    default:
      return true;
  }
}

function groupActivities(activities: LeadActivityV2[]): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  const seen = new Map<string, ActivityGroup>();

  for (const a of activities) {
    const key = a.request_id ?? `solo-${a.id}`;
    if (a.request_id && seen.has(key)) {
      seen.get(key)!.items.push(a);
    } else {
      const g: ActivityGroup = { requestId: a.request_id, items: [a] };
      groups.push(g);
      if (a.request_id) seen.set(key, g);
    }
  }

  return groups;
}

/* ── Sub-components ────────────────────────────────────────────────────── */

function ActorIcon({ actorType }: { actorType: string }) {
  const sx = { fontSize: 15, color: "#64748b" };
  if (actorType === "user")   return <UserIcon sx={sx} />;
  if (actorType === "api")    return <ApiIcon sx={sx} />;
  return <SystemIcon sx={sx} />;
}

/* Format a raw minute count into "Xd Yh Zm" — keeps only non-zero parts
   so 90 → "1h 30m", 1500 → "1d 1h", 45 → "45m". Used by the timeline's
   FieldDiffs renderer so response_delay_minutes reads as a duration
   instead of a bare integer that the rep has to mentally convert. */
function formatMinutesDuration(mins: number): string {
  if (!isFinite(mins) || mins < 0) return `${mins}`;
  if (mins === 0) return "0m";
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(" ");
}

/* Per-field value formatter. Falls back to the activity log's `label`
   (resolved server-side from STATUS_LABELS / SOURCE_LABELS / etc.), then
   to the raw value. Only override when the server's stringification is
   user-hostile — currently just the minute-count fields. */
function formatChangeValue(field: string, side: { value: unknown; label?: string | null }): string {
  if (field === "response_delay_minutes" && side.value != null && side.value !== "") {
    const n = Number(side.value);
    if (Number.isFinite(n)) return formatMinutesDuration(n);
  }
  return side.label || String(side.value ?? "—");
}

/* Per-field label override. Defaults to title-casing the snake_case
   field name; override only when the auto-derived label reads awkwardly
   alongside the formatted value (e.g. "Response Delay Minutes: 16h 12m"
   would double-state the unit). */
function fieldLabel(field: string): string {
  if (field === "response_delay_minutes") return "Response Delay";
  return field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function FieldDiffs({ changes }: { changes: Record<string, ActivityFieldChange> }) {
  const entries = Object.entries(changes);
  if (!entries.length) return null;

  return (
    <Stack spacing={0.6} sx={{ mt: 1 }}>
      {entries.map(([field, change]) => {
        const label = fieldLabel(field);
        const from = formatChangeValue(field, change.from);
        const to   = formatChangeValue(field, change.to);

        return (
          <Stack key={field} direction="row" spacing={0.5} alignItems="baseline">
            <Typography component="span" variant="caption" sx={{ color: "#cbd5e1" }}>
              •
            </Typography>
            <Typography component="span" variant="caption" sx={{ color: "#64748b", lineHeight: 1.6 }}>
              <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>{label}:</Box>
              {" "}
              <Box component="span" sx={{ color: "#dc2626" }}>{from}</Box>
              <Box component="span" sx={{ color: "#94a3b8", mx: 0.4 }}>→</Box>
              <Box component="span" sx={{ color: "#16a34a", fontWeight: 600 }}>{to}</Box>
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

function NoteBubble({ text, density = "comfortable" }: { text: string; density?: "comfortable" | "compact" }) {
  const compact = density === "compact";
  // In compact mode (narrow right-rail) we clamp to 2 lines and let the
  // rep tap to expand. Long meeting notes / call summaries would
  // otherwise dominate the rail and force scrolling for every entry.
  const [expanded, setExpanded] = useState(false);
  // Multi-line note check — single-line notes never need the toggle.
  const needsToggle = compact && (text.length > 90 || text.includes("\n"));

  return (
    <Box
      onClick={needsToggle ? () => setExpanded((v) => !v) : undefined}
      sx={{
        mt: compact ? 0.6 : 1,
        p: compact ? 0.9 : 1.5,
        bgcolor: "#fafafa",
        border: "1px solid #e5e7eb",
        borderLeft: "3px solid #f59e0b",
        borderRadius: "0 8px 8px 0",
        cursor: needsToggle ? "pointer" : "default",
        transition: "background 0.12s",
        "&:hover": needsToggle ? { bgcolor: "#f5f5f5" } : undefined,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "#374151",
          whiteSpace: "pre-wrap",
          lineHeight: compact ? 1.45 : 1.7,
          fontSize: compact ? "0.76rem" : "0.82rem",
          ...(needsToggle && !expanded ? {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          } : {}),
        }}
      >
        {text}
      </Typography>
      {needsToggle && (
        <Typography sx={{
          mt: 0.3, fontSize: "0.65rem", fontWeight: 700,
          color: "#94a3b8", letterSpacing: 0.4, textTransform: "uppercase",
        }}>
          {expanded ? "Show less" : "Show more"}
        </Typography>
      )}
    </Box>
  );
}

function GroupCard({ group, density = "comfortable" }: { group: ActivityGroup; density?: "comfortable" | "compact" }) {
  const first = group.items[0];
  const color = accentColor(first.type);
  const compact = density === "compact";

  // Compact mode drops the year + comma when the activity is from the
  // current calendar year — keeps the rail readable without truncation.
  const sameYear = new Date(first.created_at).getFullYear() === new Date().getFullYear();
  const ts = new Date(first.created_at).toLocaleString("en-US", compact
    ? (sameYear
        ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }
        : { month: "short", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit", hour12: true })
    : { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }
  );

  return (
    <Card
      elevation={0}
      sx={{
        // The Card sits inside a plain block row (no flex parent), so
        // it fills 100% width naturally — no `flex: 1` needed, and
        // explicit width:100% guards against any Card-default styling
        // that might try to size to intrinsic content.
        width: "100%",
        boxSizing: "border-box",
        // Compact mode drops the hard outline + light gutter and leans
        // on a soft tinted background instead — the narrow side-rail
        // gets too noisy with full borders on every card.
        border: compact ? "none" : "1px solid #e5e7eb",
        borderLeft: `${compact ? 2 : 3}px solid ${color}`,
        borderRadius: "0 8px 8px 0",
        p: compact ? 1.2 : 2,
        bgcolor: compact ? "#fafbfc" : "#fff",
        transition: "background 0.15s",
        "&:hover": { bgcolor: compact ? "#f5f6f8" : "#fff", boxShadow: compact ? "none" : "0 2px 8px rgba(0,0,0,0.07)" },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────
          Real staff identity: profile photo (or slate initials
          fallback) + name + role. Drives accountability — managers
          can scan the Timeline and instantly tell who logged each
          touchpoint. For system / API rows the avatar shows neutral
          initials and the actor_type icon is appended as a hint. */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: compact ? 0.9 : 1.5 }}>
        <Stack direction="row" spacing={compact ? 0.8 : 1} alignItems="center" minWidth={0}>
          <UserAvatar
            user={{
              profile_image: first.actor_profile_image ?? null,
              name:          first.actor_name ?? "?",
            }}
            size={compact ? 22 : 28}
            tooltip={false}
          />
          <Box minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700, color: NAVY, lineHeight: 1.2,
                  fontSize: compact ? "0.78rem" : "0.875rem",
                }}
                noWrap
              >
                {first.actor_name || "System"}
              </Typography>
              {first.actor_type !== "user" && (
                <Box sx={{ display: "inline-flex" }}>
                  <ActorIcon actorType={first.actor_type} />
                </Box>
              )}
            </Stack>
            {first.actor_role && (
              <Typography sx={{
                fontSize: compact ? 9.5 : 10.5,
                color: "#94a3b8", fontWeight: 600, lineHeight: 1.2, mt: 0.1,
              }} noWrap>
                {first.actor_role}
              </Typography>
            )}
          </Box>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary", whiteSpace: "nowrap", ml: 1, mt: 0.3,
            fontSize: compact ? "0.65rem" : "0.75rem",
            flexShrink: 0,
          }}
        >
          {ts}
        </Typography>
      </Stack>

      {/* ── Activity items ────────────────────────────────────── */}
      {group.items.map((activity, idx) => {
        const meta        = activity.meta ?? {};
        const isContact   = activity.type === "contact_logged";
        const contactNote = isContact && typeof meta.notes === "string"
          ? meta.notes.trim()
          : "";
        const outcome     = isContact
          ? (meta.follow_up_outcome ?? meta.outcome ?? null)
          : null;
        const taskNote    = activity.type === "task_created" && typeof meta.notes === "string"
          ? meta.notes.trim()
          : "";

        return (
          <Box
            key={activity.id}
            sx={{
              pt: idx > 0 ? (compact ? 0.9 : 1.5) : 0,
              mt: idx > 0 ? (compact ? 0.9 : 1.5) : 0,
              borderTop: idx > 0 ? "1px dashed #f1f5f9" : "none",
            }}
          >
            <Stack direction="row" alignItems="center" gap={compact ? 0.5 : 0.8} flexWrap="wrap" useFlexGap>
              {/* Contact-method icon — only shown for contact_logged rows
                  so the visual reads as "call / email / meeting / note". */}
              {isContact && contactMethodIcon(String(meta.type ?? ""))}

              <Typography variant="body2" sx={{
                fontWeight: 600, color: NAVY,
                fontSize: compact ? "0.78rem" : "0.875rem",
              }}>
                {actionLabel(activity.type, meta)}
              </Typography>

              {/* Outcome badge for contact_logged rows. Keeps the chip styling
                  consistent with the form's outcome selector. */}
              {isContact && outcome && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.3,
                  fontSize: compact ? 9 : 10, fontWeight: 700, letterSpacing: 0.3,
                  color: "#16a34a", bgcolor: "#dcfce7",
                  border: "1px solid #bbf7d0", borderRadius: "5px",
                  px: compact ? 0.45 : 0.6, py: 0.05,
                }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: compact ? 10 : 11 }} />
                  {formatOutcome(outcome)}
                </Box>
              )}
            </Stack>

            {activity.changes && Object.keys(activity.changes).length > 0 && (
              <FieldDiffs changes={activity.changes} />
            )}

            {/* Note text — rendered as a NoteBubble for any row that carries
                operator-typed context. We deliberately skip the bubble when
                the note is empty so the card never shows a hollow container. */}
            {activity.type === "note_added" && activity.description && (
              <NoteBubble text={activity.description} density={density} />
            )}
            {isContact && contactNote && (
              <NoteBubble text={contactNote} density={density} />
            )}
            {activity.type === "task_created" && taskNote && (
              <NoteBubble text={taskNote} density={density} />
            )}

            {/* Status/description caption — kept for non-note rows that
                carry a description but no changes diff and no note text. */}
            {activity.type !== "note_added" &&
              !isContact &&
              activity.type !== "task_created" &&
              activity.description &&
              (!activity.changes || Object.keys(activity.changes).length === 0) && (
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", display: "block", mt: 0.5, lineHeight: 1.6 }}
                >
                  {activity.description}
                </Typography>
              )}

            {/* Compact "Due …" line under task_created rows so the timeline
                surfaces the scheduled time alongside the note. */}
            {activity.type === "task_created" && meta.due_at && (
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                Due {new Date(meta.due_at).toLocaleString("en-US", {
                  month: "short", day: "numeric", year: "numeric",
                  hour: "numeric", minute: "2-digit", hour12: true,
                })}
              </Typography>
            )}

            {/* Audit line for SMS rows — shows the EXACT phone the
                message was dispatched to (not necessarily the lead's
                stored phone, since the composer's TO field is
                editable). Falls back to a generic message when
                metadata is missing. */}
            {(activity.type === "sms_sent" || activity.type === "sms_failed") && meta.phone && (
              <Typography variant="caption" sx={{
                color: "#64748b", display: "block", mt: 0.5,
                fontFamily: "'JetBrains Mono','SF Mono',Menlo,Consolas,monospace",
              }}>
                To {String(meta.phone)}
                {meta.ai_generated ? "  ·  AI-assisted" : ""}
              </Typography>
            )}
            {/* Body preview — first ~280 chars of the SMS, dimmed so it
                doesn't dominate the timeline. Helpful when scanning
                history without opening the SMS itself. */}
            {(activity.type === "sms_sent" || activity.type === "sms_failed") && typeof meta.preview === "string" && (
              <Typography variant="caption" sx={{
                color: "#475569", display: "block", mt: 0.4,
                fontStyle: "italic", lineHeight: 1.5,
              }}>
                "{String(meta.preview)}"
              </Typography>
            )}
          </Box>
        );
      })}
    </Card>
  );
}

/* ── Filter tabs ───────────────────────────────────────────────────────── */

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: "all",         label: "All" },
  { value: "calls",       label: "Calls" },
  { value: "emails",      label: "Emails" },
  { value: "meetings",    label: "Meetings" },
  { value: "notes",       label: "Notes" },
  { value: "status",      label: "Status" },
  { value: "assignments", label: "Assignments" },
  { value: "follow_ups",  label: "Follow-ups" },
  { value: "system",      label: "System" },
];

/* ── Main component ────────────────────────────────────────────────────── */

interface Props {
  threadId: number;
  /** "full" (default) renders the filter tabs + load-more + every group.
   *  "preview" hides the filter tabs, caps the group count at
   *  `previewLimit`, and exposes a "View full timeline →" affordance.
   *  Both modes use the SAME GroupCard / NoteBubble / date-bucket
   *  rendering — so the drawer preview and the full Timeline tab stay
   *  visually identical and any future change in one shows up in both. */
  mode?: "full" | "preview";
  /** When mode="preview", maximum number of grouped cards to render
   *  before truncating. Defaults to 5. */
  previewLimit?: number;
  /** Click handler for the "View full timeline →" link rendered at the
   *  top of preview mode. Typically switches to the drawer's Timeline
   *  tab or navigates to the lead detail page. */
  onShowMore?: () => void;
  /** "comfortable" (default) is the wide-rail rendering used inside
   *  the drawer Timeline tab and the lead detail right-rail's wider
   *  layouts. "compact" tightens every dimension — card padding,
   *  avatar size, rail gutter, NoteBubble line clamp — so the same
   *  component fits naturally inside a 400px sticky side rail. */
  density?: "comfortable" | "compact";
}

export default function LeadActivityTimeline({
  threadId, mode = "full", previewLimit = 5, onShowMore, density = "comfortable",
}: Props) {
  const isPreview = mode === "preview";
  const compact = density === "compact";
  const [activities, setActivities]   = useState<LeadActivityV2[]>([]);
  const [meta, setMeta]               = useState<PaginationMeta | null>(null);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter]           = useState<FilterTab>("all");

  const load = useCallback(async (page = 1) => {
    if (page === 1) setLoading(true); else setLoadingMore(true);
    try {
      const res = await fetchLeadActivities(threadId, page);
      setActivities((prev) => page === 1 ? res.data : [...prev, ...res.data]);
      setMeta(res.meta);
    } catch { /* silent */ }
    if (page === 1) setLoading(false); else setLoadingMore(false);
  }, [threadId]);

  useEffect(() => { load(1); }, [load]);

  const groups = useMemo(() => {
    const filtered = filter === "all"
      ? activities
      : activities.filter((a) => matchesFilter(a, filter));
    const all = groupActivities(filtered);
    return isPreview ? all.slice(0, previewLimit) : all;
  }, [activities, filter, isPreview, previewLimit]);

  // Total un-capped group count — used for the "+N more" label inside
  // the preview's show-more link so the rep knows how much is hidden.
  const totalGroupCount = useMemo(
    () => groupActivities(activities).length,
    [activities]
  );

  /* ── Loading state ──────────────────────────────────────────── */
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: isPreview ? 3 : 8 }}>
        <CircularProgress size={22} thickness={4} sx={{ color: "#c9a96e" }} />
      </Box>
    );
  }

  /* ── Empty state ────────────────────────────────────────────── */
  if (activities.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: isPreview ? 3 : 8 }}>
        <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
          No activity recorded yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Filter tabs (full mode only — preview omits to keep the
          drawer's Details tab compact) ───────────────────────────── */}
      {!isPreview && (
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fafafa",
          borderBottom: "1px solid #f1f5f9",
          mb: 2.5,
        }}
      >
        <Tabs
          value={filter}
          onChange={(_, v) => setFilter(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 38,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 38,
              fontSize: "0.8rem",
              color: "#64748b",
              px: 1.5,
            },
            "& .Mui-selected": { color: `${NAVY} !important`, fontWeight: 700 },
            "& .MuiTabs-indicator": { bgcolor: "#c9a96e", height: 2 },
          }}
        >
          {FILTER_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} />
          ))}
        </Tabs>
      </Box>
      )}

      {/* ── Preview-mode show-more affordance ──────────────────────
          Appears above the timeline in the drawer Details tab so the
          rep always knows there's more behind it. Click switches to
          the dedicated Timeline tab where the same component renders
          in full mode with filter tabs + pagination. */}
      {isPreview && onShowMore && totalGroupCount > previewLimit && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.2 }}>
          <Button
            size="small"
            onClick={onShowMore}
            sx={{
              textTransform: "none",
              fontSize: 11,
              fontWeight: 700,
              color: "#c9a96e",
              letterSpacing: 0.4,
              minWidth: 0,
              px: 0.6,
              "&:hover": { color: NAVY, bgcolor: "transparent" },
            }}
          >
            +{totalGroupCount - previewLimit} more · view full timeline →
          </Button>
        </Stack>
      )}

      {/* ── Empty filter state ───────────────────────────────────── */}
      {groups.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
            No {filter} activity found
          </Typography>
        </Box>
      )}

      {/* ── Timeline ────────────────────────────────────────────── */}
      {groups.length > 0 && (() => {
        // Bucket the (already-grouped-by-request) groups into Today /
        // Yesterday / This Week / Older. Buckets render as sticky
        // section headers so the rep can scan by "when" without
        // counting timestamps. Empty buckets are skipped — never show
        // a header without rows under it.
        const ORDER: DateBucket[] = ["today", "yesterday", "this_week", "older"];
        const bucketed: Record<DateBucket, typeof groups> = {
          today: [], yesterday: [], this_week: [], older: [],
        };
        groups.forEach((g) => bucketed[bucketOf(g.items[0].created_at)].push(g));

        return (
          <Box sx={{ position: "relative", pl: compact ? "24px" : "36px" }}>
            {/* Continuous vertical rail through every bucket — slimmer
                and softer in compact mode so it doesn't dominate the
                narrow side-rail visually. */}
            <Box
              sx={{
                position: "absolute",
                left: compact ? 6 : 10,
                top: 12,
                bottom: 12,
                width: compact ? 1 : 2,
                bgcolor: compact ? "#eef0f4" : "#e2e8f0",
                borderRadius: 1,
              }}
            />

            {ORDER.map((b) => {
              const inBucket = bucketed[b];
              if (inBucket.length === 0) return null;
              return (
                <Box key={b} sx={{ mb: compact ? 1 : 1.5 }}>
                  {/* Bucket header — small, muted, uppercase. */}
                  <Typography sx={{
                    fontSize: compact ? 9 : 10,
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    ml: 0,
                    mb: compact ? 0.5 : 0.8,
                  }}>
                    {BUCKET_LABEL[b]} · {inBucket.length}
                  </Typography>

                  {inBucket.map((group) => {
                    const color = accentColor(group.items[0].type);
                    return (
                      // Block layout — the previous `display: flex` row
                      // with an absolutely-positioned dot caused the
                      // GroupCard's `flex: 1` width to collapse inside
                      // the narrow 400px sticky rail (intrinsic-min-
                      // width quirk with two flex-items where one is
                      // out-of-flow). Plain block + width:100% on the
                      // Card sidesteps the entire flex calculation and
                      // restores full card width inside the side rail.
                      <Box
                        key={group.requestId ?? group.items[0].id}
                        sx={{ position: "relative", mb: compact ? 1.2 : 2 }}
                      >
                        {/* Timeline dot — anchored to THIS row so it
                            stays vertically aligned with the card's top.
                            Negative left pulls it into the gutter
                            reserved by the outer `pl: 24px`. */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: compact ? "-23px" : "-33px",
                            top: compact ? 8 : 12,
                            width: compact ? 11 : 16,
                            height: compact ? 11 : 16,
                            borderRadius: "50%",
                            bgcolor: "#fff",
                            border: `${compact ? 2 : 3}px solid ${color}`,
                            zIndex: 1,
                          }}
                        />

                        {/* Group card */}
                        <GroupCard group={group} density={density} />
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* ── Load more (full mode only — preview routes to Timeline tab) ── */}
      {!isPreview && meta && meta.current_page < meta.last_page && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => load(meta.current_page + 1)}
            disabled={loadingMore}
            startIcon={
              loadingMore
                ? <CircularProgress size={12} thickness={4} />
                : undefined
            }
            sx={{
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#64748b",
              fontSize: "0.8rem",
              "&:hover": { borderColor: "#c9a96e", color: NAVY, bgcolor: "transparent" },
            }}
          >
            Load more activity
          </Button>
        </Box>
      )}
    </Box>
  );
}
